import { FC, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDate, splitDateTime } from "capitalrx-utilities";
import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";

import { MemberClaimListRequest, useListMemberClaimsQuery } from "@/App/api/member";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { Logo, PrintableWrapper, ThemedButton } from "@/App/components";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { ExperienceContext } from "@/App/context";

import "./PrintClaims.scss";

type MutableClaimListRequest = {
  -readonly [K in keyof MemberClaimListRequest]: MemberClaimListRequest[K];
};

export const PrintPharmacyClaimsPage: FC = () => {
  const { i18n, t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);
  const { first_name, last_name, external_member_id, birth_date } = memberEligibility.member;
  const [searchParams] = useSearchParams();
  const language: string = i18n.language ?? "en";
  const now = DateTime.now().setLocale(language);
  const formattedDate = now.toLocaleString(DateTime.DATE_FULL);
  const ytd = searchParams.get("ytd");
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const personCode = searchParams.get("person_code");
  const medicarePrescriptionPaymentPlanIndicator = searchParams.get("medicare_prescription_payment_plan_indicator");
  const printRef = useRef();

  const listClaimsFilters: MemberClaimListRequest = useMemo(() => {
    // we intend to grab all relevant claims since this will be printed and there are no paging controls
    const filters: MutableClaimListRequest = {
      results_per_page: 1000,
      order_by: [{ "date_of_service": "desc" }],
    };
    if (ytd) {
      filters.year_to_date = true;
    } else if (!month && year) {
      filters.year = +year;
    } else {
      filters.month = +month;
      filters.year = +year;
    }

    if (personCode) {
      filters.person_code = personCode;
    }

    if (medicarePrescriptionPaymentPlanIndicator == "Y") {
      filters.coverage_strategy_type = "medicare_prescription_payment_plan";
    }

    return { ...filters };
  }, []);

  const { data: claims, isLoading: isLoadingClaims } = useListMemberClaimsQuery(listClaimsFilters);

  const tableRows = useMemo(() => {
    return (claims?.results || []).map(({ claim }, index: number) => ({
      key: index,
      date_of_service: claim.date_of_service,
      drug_name: claim.drug_name,
      pharmacy: claim.pharmacy_name,
      qty: claim.quantity_dispensed,
      days_supply: claim.days_supply,
      member_paid: claim.patient_pay_amount,
      plan_paid: claim.plan_pay_amount,
    }));
  }, [claims]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.printPharmacyClaimsHistory);
    handlePrint();
  }, [handlePrint]);

  const pageDescription = useMemo(() => {
    let generateDate: DateTime;
    let when: string;
    if (ytd) {
      generateDate = now;
      when = `${translate("PRINT_CLAIMS_PAGE.currentYearToDate")} - ${generateDate.year}`;
    } else if (!month && year) {
      generateDate = DateTime.fromObject({
        year: +year,
        month: 1,
      });
      when = `${generateDate.year}`;
    } else {
      generateDate = DateTime.fromObject({
        year: +year,
        month: +month,
      });
      when = `${translate(`PRINT_CLAIMS_PAGE.${generateDate.monthLong.toLowerCase()}`)} ${generateDate.year}`;
    }

    let prefixed = translate("PRINT_CLAIMS_PAGE.allClaimsFor");
    if (medicarePrescriptionPaymentPlanIndicator === "Y") {
      prefixed = translate("PRINT_CLAIMS_PAGE.allMedicarePrescriptionPaymentPlan");
    }

    return (
      <Body>
        {prefixed} <Semibold>{when}</Semibold>
      </Body>
    );
  }, [medicarePrescriptionPaymentPlanIndicator, month, ytd]);

  return (
    <LoadingGroup loading={isLoadingClaims}>
      <PrintableWrapper ref={printRef}>
        <Stack style={{ height: "100%" }}>
          <Shelf alignment="top">
            <Logo />
            <Shelf.Spacer />
            <ThemedButton
              testId="print-pharmacy-claims-history"
              className="print-button"
              onClick={onPrint}
              appearance="text"
              icon="print"
            />
          </Shelf>
          <div>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberName")}</Semibold> {first_name} {last_name}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.dateOfBirth")}</Semibold> {formatDate(birth_date, "MM/DD/YYYY")}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberId")}</Semibold> {external_member_id}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.personCode")}</Semibold> {personCode}
            </Body>
          </div>
          <Shelf>
            <Body size="medium">
              <Semibold>{translate("PRINT_CLAIMS_PAGE.pharmacyClaimsSummary")}</Semibold>
            </Body>
            <Shelf.Spacer />
            <Body>{`${translate("PRINT_CLAIMS_PAGE.prepared")} ${formattedDate}`}</Body>
          </Shelf>
          {pageDescription}
          <LoadingGroup.Placeholder
            fallback={
              <Shelf alignment="center">
                <Stack alignment="center">
                  <Spinner size="large" />
                </Stack>
              </Shelf>
            }
          >
            <table className="claims-history-table">
              <thead>
                <tr>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.dateOfService")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.drugName")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.pharmacy")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.qty")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.daysSupply")}</Semibold></Body>
                  </th>
                  <th>
                    <Body><Semibold>{translate("PRINT_CLAIMS_PAGE.memberPaid")}</Semibold></Body>
                  </th>
                  <th>
                    <Body textAlign="right"><Semibold>{translate("PRINT_CLAIMS_PAGE.planPaid")}</Semibold></Body>
                  </th>
                </tr>
              </thead>
              <tbody data-testid="table-claims">
                {tableRows.map((claim) => {
                  return (
                    <tr data-testid={`claim-${claim.key}-row`} key={claim.key}>
                      <td data-testid={`claim-${claim.key}-date-of-service`}><Body>{splitDateTime(claim.date_of_service)}</Body></td>
                      <td data-testid={`claim-${claim.key}-drug-name`}><Body>{claim.drug_name}</Body></td>
                      <td data-testid={`claim-${claim.key}-pharmacy`}><Body>{claim.pharmacy}</Body></td>
                      <td data-testid={`claim-${claim.key}-quantity`}><Body>{claim.qty}</Body></td>
                      <td data-testid={`claim-${claim.key}-days-supply`}><Body>{claim.days_supply}</Body></td>
                      <td data-testid={`claim-${claim.key}-member-paid`}><Body>${claim.member_paid || 0}</Body></td>
                      <td data-testid={`claim-${claim.key}-plan-paid`}><Body textAlign="right">${claim.plan_paid || 0}</Body></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </LoadingGroup.Placeholder>
          <Stack.Spacer />
          <Stack spacing="small">
            <Body size="medium"><Semibold>{translate("PRINT_CLAIMS_PAGE.customerSupport")}</Semibold></Body>
            <Body testId="customer-support-number">
              {translate("PRINT_CLAIMS_PAGE.contactCustomerCareAt")} <Semibold>{customerSupportNumber}</Semibold>.
            </Body>
          </Stack>
        </Stack>
      </PrintableWrapper>
    </LoadingGroup>
  );
};
